// Splits with the linked partner (piece 2): "Split this bill?" in the edit sheet, the split
// sheet (my share in percent, shown in dollars), the Requests card on Overview with its Accept
// sheet, and the line under a split purchase. Nothing here shows without an active partner link,
// except the line on a purchase that was split before (history).
import type { IncomingSplitDto, ItemDto, OverviewDto, TransactionDto } from '../../../shared/api';
import { splitsEnabled } from '../../../shared/partner';
import { ACCEPT_SET_ASIDE, ALL_THEIRS, ALL_THEIRS_LINE, ALL_THEIRS_PERCENT, SPLIT_DEFAULT_PERCENT, SPLIT_STATUS, splitAllowed, splitLine, splitShares, splitSmallPrint } from '../../../shared/splits';
import { esc, money } from '../format';
import { noteField } from './notes';
import { NOTE_FROM_PARTNER, noteFromSplit } from '../../../shared/notes';
import { SHARE_COUNTS_IN } from '../../../shared/months';
import { spendingCategories } from './items';

/** Can this purchase be split now? Spending, or an entry of a transfer item; not already split, not my share of theirs. */
export function canSplit(d: OverviewDto, t: TransactionDto): boolean {
  if (!splitsEnabled(d.partner) || t.spread) return false;
  if (t.split && (t.split.direction === 'received' || (t.split.status === 'pending' && !t.split.endedReason) || (t.split.status === 'accepted' && t.split.endedReason !== 'removed'))) return false;
  // Money set aside only as an entry of a transfer item (shared splitAllowed()).
  const setAside = !!t.categoryId && d.categories.find((k) => k.id === t.categoryId)?.kind === 'set_aside';
  const itemKind = t.itemId ? (d.items.find((i) => i.id === t.itemId)?.kind ?? null) : null;
  if (!splitAllowed({ source: t.source, setAside, itemKind })) return false;
  return t.amountCents >= 2;
}

/** In the edit sheet: the split's status and what can be done, or "Split this bill?". */
export function splitBlock(d: OverviewDto, t: TransactionDto): string {
  const s = t.split;
  const line = s ? `<p class="hint splitline"><b>${esc(SPLIT_STATUS[s.status])}</b> ${esc(splitLine(s))}${s.note ? `<br>Note: ${esc(s.note)}` : ''}</p>` : '';
  let action = '';
  if (s?.direction === 'sent' && s.status === 'pending') action = `<button class="linkbtn" data-a="split-cancel" data-id="${esc(s.id)}">Cancel the request</button>`;
  else if (s?.direction === 'sent' && s.status === 'accepted' && s.endedReason !== 'removed')
    action = `<button class="linkbtn" data-a="split-remove" data-id="${esc(s.id)}">Remove the split</button>`;
  else if (canSplit(d, t)) action = `<button class="btn" data-a="split-open">Split this bill?</button>`;
  return line || action ? `<div class="splitblock">${line}${action}</div>` : '';
}

/** The small print under a split purchase wherever its amount is the main number. */
export const splitRowLine = (t: TransactionDto) => (t.split ? `<span class="tsplit">${esc(splitSmallPrint(t.split))}</span>` : '');

/**
 * The edit sheet's header amount: my share as the main number for a split, the full bill as the
 * small print. Nothing for a purchase that was never split (the Amount field says it all).
 */
export const sheetAmount = (t: TransactionDto) =>
  t.split ? `<p class="sheetamt"><b>${money(t.countedCents, 2)}</b> ${splitRowLine(t)}</p>` : '';

/** The two shares, as the sheet shows them. */
export function sharesText(fullCents: number, myPercent: number, partner: string): string {
  const s = splitShares(fullCents, myPercent);
  if (s && s.mineCents === 0) return `${ALL_THEIRS(partner)}: ${partner} ${money(s.theirsCents, 2)}. ${ALL_THEIRS_LINE(partner)}`;
  return s ? `You ${money(s.mineCents, 2)}, ${partner} ${money(s.theirsCents, 2)}` : 'Too small to split this way.';
}

/**
 * The share chips: 50/50, my percent, and "All Tiffany's" (0%, migration 0046) unless `allowAll` is
 * off (the Done sheet: a transfer item's entry is my own plan). `attrs` is each chip's action.
 */
function shareChips(partner: string, pct: number, allowAll: boolean, attrs: string): string {
  const chip = (p: number, label: string) => `<button class="chip" ${attrs} data-pct="${p}" aria-pressed="${p === pct}">${esc(label)}</button>`;
  return `<div class="chips">${[50, 60, 70, 40, 30].map((p) => chip(p, p === 50 ? '50/50' : `${p}% me`)).join('')}${
    allowAll ? chip(ALL_THEIRS_PERCENT, ALL_THEIRS(partner)) : ''
  }</div>`;
}

/**
 * The percent picker and my share in dollars: shared by the split sheet, the add sheet and the
 * Done sheet. There is no split note field: the request carries the purchase's own "What was this
 * for?" (shared/notes.ts splitNoteOf()), shown with "Tiffany will see this." under it.
 */
function pickerFields(fullCents: number | null, partner: string, pct = SPLIT_DEFAULT_PERCENT, amountField = 'f-amt', allowAll = true): string {
  const amountAttr = amountField === 'f-amt' ? '' : ` data-amt="${esc(amountField)}"`;
  return `${shareChips(partner, pct, allowAll, 'data-a="split-pct"')}
  <label class="fld"><span>My share (%)</span><input id="sp-pct" type="number" inputmode="numeric" min="${allowAll ? 0 : 1}" max="99" step="1" value="${pct}"${fullCents === null ? '' : ` data-full="${fullCents}"`}${amountAttr} data-partner="${esc(partner)}"></label>
  <p class="hint" id="sp-shares">${esc(fullCents === null ? 'Enter the amount above to see the two shares.' : sharesText(fullCents, pct, partner))}</p>`;
}

/** The Done sheet's full amount for my share of the plan: $140 at 70% is $200 (whole cents, rounded). */
export const grossFor = (shareCents: number, myPercent: number) => Math.round((shareCents * 100) / myPercent);

/**
 * The Done sheet's "Split with Tiffany" on a transfer item (money set aside, 70/30): a switch, on
 * by default once the item remembers a percent (`splitPercent`), at that percent. The picker reads
 * the amount being typed (d-amt) and shows both shares before anything is sent. It goes with no
 * note: a Done entry has no "What was this for?" (Done never writes one), and there is no separate
 * split note. Only with an active link, never for a yearly spread item.
 */
export function doneSplitBlock(d: OverviewDto, item: ItemDto, planCents: number, amountCents: number): string {
  if (!splitsEnabled(d.partner) || item.kind !== 'transfer' || item.spread) return '';
  const partner = d.partner.linked!.name;
  const on = item.splitPercent != null;
  const pct = item.splitPercent ?? SPLIT_DEFAULT_PERCENT;
  return `<div class="nopt" id="d-split-box"><label class="noptrow nswitch"><span>${esc(`Split with ${partner}`)}</span><input type="checkbox" role="switch" class="switch" id="d-split" data-plan="${planCents}"${on ? ' checked' : ''}></label>
  <div class="noptbody" id="d-split-fields"${on ? '' : ' hidden'}>${pickerFields(null, partner, pct, 'd-amt', false).replace(
    esc('Enter the amount above to see the two shares.'),
    esc(sharesText(amountCents, pct, partner)),
  )}
  <p class="hint">${esc(`Your budget counts only your share. ${partner} gets a request for hers.`)}</p></div></div>`;
}

/**
 * The add sheet's "Split this bill" (a hand-added purchase): off by default; on, the same picker
 * as the edit sheet's split. Only with an active partner link; the add sheet files to spending
 * only, and a yearly spread item hides it (main.ts updateAddSplit()).
 */
export function addSplitBlock(d: OverviewDto): string {
  if (!splitsEnabled(d.partner)) return '';
  const partner = d.partner.linked!.name;
  return `<div class="splitadd" id="f-split-box">
  <label class="check"><input type="checkbox" id="f-split"> Split this bill with ${esc(partner)}</label>
  <div id="f-split-fields" hidden>${pickerFields(null, partner)}
  <p class="hint">Your budget counts only your share while ${esc(partner)} decides.</p></div></div>`;
}

/**
 * A New purchases row's split choice (main.ts ui.newSplits): absent is off. Its note is the row's
 * "What was this for?" as Confirm sends it (rowNote()), never a field of its own.
 */
export interface NewSplit {
  pct: number;
}

/**
 * Can a purchase Hush logged by itself (an emailed alert or an auto-logged charge) be split as it
 * is confirmed on the New purchases card? The same rules as the edit sheet's split (canSplit():
 * linked, spending, not a transfer, set aside or a yearly spread item, not split already).
 */
export const canSplitNew = (d: OverviewDto, t: TransactionDto) => (t.source === 'email' || t.source === 'recurring') && canSplit(d, t);

/**
 * "Split this bill with Tiffany" on a New purchases row: off by default; on, the same percents,
 * my share in dollars (the note is the row's "What was this for?"). Confirming files the purchase (and its tip), then sends the
 * request. `fullCents` is the bill it shares: the charge plus the row's tip.
 */
export function newSplitBlock(d: OverviewDto, t: TransactionDto, s: NewSplit | undefined, fullCents = t.amountCents): string {
  if (!canSplitNew(d, t)) return '';
  const partner = d.partner.linked!.name;
  const id = esc(t.id);
  const pct = s?.pct ?? SPLIT_DEFAULT_PERCENT;
  const fields = s
    ? `${shareChips(partner, pct, true, `data-a="new-split-pct" data-id="${id}"`)}
  <label class="fld"><span>My share (%)</span><input type="number" inputmode="numeric" min="0" max="99" step="1" value="${pct}" data-new-split-pct="${id}" data-full="${fullCents}" data-partner="${esc(partner)}"></label>
  <p class="hint" data-new-split-shares="${id}">${esc(sharesText(fullCents, pct, partner))}</p>`
    : '';
  // One row with a switch (the Edit sheet of a New purchases row); on, the picker.
  return `<div class="nopt newsplit"><label class="noptrow nswitch"><span>Split with ${esc(partner)}</span><input type="checkbox" role="switch" class="switch" data-new-split="${id}"${s ? ' checked' : ''}></label>${fields ? `<div class="noptbody">${fields}</div>` : ''}</div>`;
}

/**
 * "Split with Tiffany" (from the edit sheet): my share in percent (50/50 to start), the purchase's
 * "What was this for?" (`note`: what the edit sheet's field held, so nothing typed there is lost),
 * Send. Send saves that note on the purchase when it changed, then sends it with the request.
 */
export function vSplitSheet(d: OverviewDto, t: TransactionDto, note: string | null = t.note): string {
  const partner = d.partner.linked?.name ?? 'your partner';
  return `<div class="grab"></div><h2>Split with ${esc(partner)}</h2>
  <p class="hint">${esc(t.merchant)}, ${money(t.amountCents + (t.tipCents ?? 0), 2)}. Only the name, the amount, the two shares and what it was for go to ${esc(partner)}. Never your category, card or other purchases.</p>
  ${pickerFields(t.amountCents + (t.tipCents ?? 0), partner)}
  ${noteField('f-note', note, '', { partner, on: true })}
  <p class="hint">Your budget counts only your share while ${esc(partner)} decides. If they decline, the full amount comes back.</p>
  <p class="err" id="sp-err"></p>
  <div class="btns"><button class="btn primary" data-a="split-send">Send</button><button class="btn" data-a="close">Not now</button></div>`;
}

/** The Requests card on Overview: pending requests from the partner, with Accept and Decline. */
export function vRequestsCard(d: OverviewDto): string {
  if (!splitsEnabled(d.partner) || !d.splitRequests.length) return '';
  const rows = d.splitRequests
    .map(
      (r) => `<div class="askrow splitreq" data-split="${esc(r.id)}"><div class="askhead"><span><b>${esc(r.fromName)}</b> split ${esc(r.merchant)}</span><span>${money(r.yourShareCents, 2)}</span></div>
  <p class="hint">Your share of ${money(r.fullCents, 2)}.${r.note ? ` Note: ${esc(r.note)}` : ''}</p>
  <div class="btns"><button class="btn primary" data-a="split-accept-open" data-id="${esc(r.id)}">Accept</button><button class="btn" data-a="split-decline" data-id="${esc(r.id)}">Decline</button></div></div>`,
    )
    .join('');
  return `<section class="ask requests"><h2>Requests</h2>${rows}<div class="btns"><button class="linkbtn" data-a="settle-screen">See all splits</button></div></section>`;
}

/**
 * What the hourly cron did to a split when it replaced an auto-logged charge with the real alert
 * (src/services/placeholder-splits.ts): sent again, or kept on the logged one (Open to sort it out).
 * Shown even when unlinked; OK hides it.
 */
export function vSplitNotices(d: OverviewDto): string {
  const notices = d.splitNotices ?? [];
  if (!notices.length || d.isSample) return '';
  const rows = notices
    .map(
      (n) => `<div class="askrow" data-notice="${esc(n.id)}"><p class="hint">${esc(n.text)}</p>
  <div class="btns">${n.kind === 'kept' ? `<button class="btn" data-a="edit" data-id="${esc(n.transactionId)}">Open</button>` : ''}<button class="btn" data-a="split-notice-ok" data-id="${esc(n.id)}">OK</button></div></div>`,
    )
    .join('');
  return `<section class="ask"><h2>Split update</h2>${rows}</section>`;
}

/**
 * "Or money set aside", folded: a row across the sheet's width that opens the set-aside pills.
 * Shows the picked one's name, if any. There the share counts as set aside, never spending, and
 * its items are my transfer items.
 */
function asideRow(pickedName: string | null, open: boolean, pills: string): string {
  return `<div class="asidepick"><button class="asidehead" data-a="split-accept-aside" aria-expanded="${open}" aria-controls="sa-aside"><span class="asidelabel">Or money set aside</span>${
    pickedName ? `<span class="asidesel">${esc(pickedName)}</span>` : ''
  }<span class="chev" aria-hidden="true">${open ? '&#9662;' : '&#9656;'}</span></button><div class="chips" id="sa-aside"${open ? '' : ' hidden'}>${pills}</div></div>`;
}

/**
 * Accept: which of my categories it goes in (Hush's guess picked), and an item if I want one. My
 * money set aside is offered too, folded under one "Or money set aside" row (whole row taps,
 * chevron, aria-expanded, like History on the Splits screen): the row names a picked set-aside
 * category and starts open while one is picked (`asideOpen` undefined), so the pick is always seen.
 */
export const asideStartsOpen = (d: OverviewDto, categoryId: string | null, asideOpen?: boolean) =>
  asideOpen ?? (!!categoryId && d.categories.find((k) => k.id === categoryId)?.kind === 'set_aside');

/** The Accept sheet's state: the request, the category and item picked, and the money set aside fold. */
export interface AcceptState {
  id: string;
  cat: string | null;
  item: string | null;
  /** Opened or folded by hand; undefined follows the pick (asideStartsOpen()). */
  asideOpen?: boolean;
  /** "What was this for?" as the field holds it: starts at the request's note, then as typed. */
  note: string;
}

/**
 * The Accept sheet as it opens, the one way in: every entry (the Requests card's Accept, which a
 * split notification or email opens Overview on) starts here, so the fold is the same everywhere.
 */
export const acceptStart = (r: IncomingSplitDto): AcceptState => ({ id: r.id, cat: r.suggestedCategoryId, item: null, note: noteFromSplit(r.note) ?? '' });

/**
 * My share's "What was this for?" on the Accept sheet: the same field as everywhere (chips from my
 * own notes for this merchant), prefilled with the request's note and "From Tiffany, edit if you
 * like." under it; empty and no line when the request has none. It is saved on my purchase only.
 */
function acceptNote(r: IncomingSplitDto, note: string): string {
  const hint = noteFromSplit(r.note) ? `<p class="hint notefrom">${esc(NOTE_FROM_PARTNER(r.fromName))}</p>` : '';
  return noteField('sa-note', note, ' data-accept-note="1"').replace('<div class="notechips"', `${hint}<div class="notechips"`);
}

/** The Accept sheet for a state, or '' when the request is gone. */
export function acceptSheetFor(d: OverviewDto, a: AcceptState): string {
  const r = d.splitRequests.find((x) => x.id === a.id);
  return r ? vAcceptSheet(d, r, a.cat, a.item, a.asideOpen, a.note) : '';
}

export function vAcceptSheet(d: OverviewDto, r: IncomingSplitDto, categoryId: string | null, itemId: string | null, asideOpen?: boolean, note: string = noteFromSplit(r.note) ?? ''): string {
  const picked = categoryId ? d.categories.find((k) => k.id === categoryId) : undefined;
  const setAside = picked?.kind === 'set_aside';
  const items = categoryId ? d.items.filter((i) => i.categoryId === categoryId && (i.kind === 'transfer') === setAside && !i.archived) : [];
  const chips = (ks: typeof d.categories) =>
    ks.map((k) => `<button class="chip" data-a="split-accept-pick" data-cat="${esc(k.id)}" aria-pressed="${categoryId === k.id}">${esc(k.name)}</button>`).join('');
  const aside = d.categories.filter((k) => k.kind === 'set_aside');
  return `<div class="grab"></div><h2>Accept ${money(r.yourShareCents, 2)}</h2>
  <p class="hint">${esc(r.fromName)} paid ${money(r.fullCents, 2)} at ${esc(r.merchant)}. Your share goes into your own budget as a purchase. ${esc(SHARE_COUNTS_IN(r.shareDate))}</p>
  <div class="fld"><span>Category</span><div class="chips">${chips(spendingCategories(d))}</div></div>
  ${acceptNote(r, note)}
  ${aside.length ? asideRow(setAside ? picked!.name : null, asideStartsOpen(d, categoryId, asideOpen), chips(aside)) : ''}
  ${setAside ? `<p class="hint">${esc(ACCEPT_SET_ASIDE)}</p>` : ''}
  ${
    items.length
      ? `<div class="fld"><span>Item (optional)</span><div class="chips"><button class="chip" data-a="split-accept-item" data-item="" aria-pressed="${!itemId}">None</button>${items
          .map((i) => `<button class="chip" data-a="split-accept-item" data-item="${esc(i.id)}" aria-pressed="${itemId === i.id}">${esc(i.name)}</button>`)
          .join('')}</div></div>`
      : ''
  }
  <p class="err" id="sa-err"></p>
  <div class="btns"><button class="btn primary" data-a="split-accept" data-id="${esc(r.id)}"${categoryId ? '' : ' disabled'}>Accept</button><button class="btn" data-a="close">Not now</button></div>`;
}
